import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuthContext } from '../../../context/AuthContext'
import { StatusBadge } from '../../components/common/StatusBadge'
import { RunTypeBadge } from '../../components/common/RunTypeBadge'
import { EvalApiService } from '../../services/EvalApiService'
import { EvalGtSample, GtSampleRunEntry, LABEL_SOURCE_LABELS, odMeanF1, prTop1Accuracy } from '../../types'
import { fmtIso } from '../../utils'
import { fmtPct } from '../../metrics'
import { LABEL_SOURCE_STYLES } from '../../constants'

function countFields(inf: EvalGtSample): Array<{ field: string; count: number }> {
  const pr = inf.pr_inference
  if (!pr) return []
  const fields = ['product_polygons', 'carton_polygons', 'price_tag_boxes', 'shelf_boxes', 'shelf_edges', 'top_stock', 'merchandise']
  return fields
    .map((field) => ({ field, count: Array.isArray(pr[field]) ? (pr[field] as unknown[]).length : 0 }))
    .filter((f) => f.count > 0)
}

export const GtSamplePage: React.FC = () => {
  const { gtSampleId } = useParams<{ gtSampleId: string }>()
  const { token } = useAuthContext()

  const [sample, setSample] = useState<EvalGtSample | null>(null)
  const [runs, setRuns] = useState<GtSampleRunEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [corrField, setCorrField] = useState('')
  const [corrNote, setCorrNote] = useState('')
  const [applying, setApplying] = useState(false)
  const [corrApplied, setCorrApplied] = useState(false)

  useEffect(() => {
    if (!gtSampleId) return
    Promise.all([
      EvalApiService.getGtSample(token ?? '', gtSampleId),
      EvalApiService.getGtSampleRuns(token ?? '', gtSampleId),
    ]).then(([inf, { items }]) => {
      setSample(inf)
      setRuns(items)
      setLoading(false)
    })
  }, [gtSampleId, token])

  const handleApplyCorrection = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!sample || !corrField.trim()) return
    setApplying(true)
    await EvalApiService.correctGtSample(token ?? '', sample.gt_sample_id, {
      field: corrField.trim(),
      value: null,
      note: corrNote.trim() || undefined,
    })
    setCorrField('')
    setCorrNote('')
    setCorrApplied(true)
    setApplying(false)
    setTimeout(() => setCorrApplied(false), 3000)
  }

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      </div>
    )
  }

  if (!sample) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>GT Sample not found.</div>
      </div>
    )
  }

  const fieldCounts = countFields(sample)

  return (
    <div className="page-content">
      {sample.dataset_id && (
        <div style={{ marginBottom: 12 }}>
          <Link
            to={`/eval/datasets/${sample.dataset_id}`}
            style={{ fontSize: 12, color: 'var(--text-secondary)', textDecoration: 'none' }}
          >
            ← Back to dataset
          </Link>
        </div>
      )}
      <div className="eval-page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="eval-page-title" style={{ fontFamily: 'var(--font-mono)' }}>
              {sample.gt_sample_id}
            </div>
            <span style={LABEL_SOURCE_STYLES[sample.label_source]}>
              {LABEL_SOURCE_LABELS[sample.label_source]}
            </span>
          </div>
          <div className="eval-page-sub" style={{ fontFamily: 'var(--font-mono)' }}>
            {sample.image_id != null ? `image_id: ${sample.image_id}` : null}
            {sample.store_id !== null && ` · store_id: ${sample.store_id}`}
            {sample.camera_id !== null && ` · camera_id: ${sample.camera_id}`}
          </div>
        </div>
        <button className="eval-btn" onClick={() => window.open(`/gt/audit/${sample.gt_sample_id}`, '_blank')}>
          Edit Sample
        </button>
      </div>

      <div className="eval-two-col" style={{ marginBottom: 32 }}>
        <div className="eval-viewer-placeholder">
          {sample.image_url
            ? <img src={sample.image_url} alt={`image_id: ${sample.image_id}`} style={{ width: '100%', borderRadius: 4 }} />
            : <div className="eval-viewer-placeholder-title">No image available</div>
          }
          {fieldCounts.length > 0 && (
            <div className="eval-viewer-counts">
              {fieldCounts.map((f) => (
                <div key={f.field}>
                  {f.field}: <strong>{f.count}</strong>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="eval-correction-panel">
          <div className="eval-correction-panel-title">Apply Correction</div>
          <form className="eval-form" style={{ maxWidth: 'none' }} onSubmit={handleApplyCorrection}>
            <div className="eval-form-group">
              <label className="eval-form-label">Field</label>
              <input
                className="eval-form-input"
                type="text"
                value={corrField}
                onChange={(e) => setCorrField(e.target.value)}
                placeholder="e.g. product_polygons"
                required
              />
            </div>
            <div className="eval-form-group">
              <label className="eval-form-label">Note (optional)</label>
              <textarea
                className="eval-form-textarea"
                value={corrNote}
                onChange={(e) => setCorrNote(e.target.value)}
                placeholder="Describe the correction..."
              />
            </div>
            <div>
              <button
                type="submit"
                className="eval-btn"
                disabled={applying || !corrField.trim()}
              >
                {applying ? 'Applying...' : 'Apply Correction'}
              </button>
              {corrApplied && (
                <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--positive)' }}>
                  Correction recorded.
                </span>
              )}
            </div>
          </form>

          {(sample.corrections ?? []).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Correction History
              </div>
              <table className="eval-score-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>By</th>
                    <th>When</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(sample.corrections ?? []).map((c, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'var(--font-mono)' }}>{c.field}</td>
                      <td>{c.corrected_by}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{fmtIso(c.corrected_at)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{c.note ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Score History
        </div>
        <table className="eval-score-table">
          <thead>
            <tr>
              <th>Run ID</th>
              <th>Type</th>
              <th>Status</th>
              <th>Triggered At</th>
              <th>Mean F1</th>
              <th>PR Top1</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((entry) => (
              <tr key={entry.run_id}>
                <td>
                  <Link
                    to={`/eval/runs/${entry.run_id}`}
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}
                  >
                    {entry.run_id}
                  </Link>
                </td>
                <td><RunTypeBadge runType={entry.run_type} /></td>
                <td><StatusBadge status={entry.status} /></td>
                <td style={{ color: 'var(--text-secondary)' }}>{fmtIso(entry.triggered_at)}</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{fmtPct(odMeanF1(entry.metrics.OD))}</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{fmtPct(prTop1Accuracy(entry.metrics.PR?.products))}</td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}>
                  No evaluation runs have processed this sample yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
